import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';

export const AdminSiteConfigLimitsSettings = () => {
  const [dailyLimit, setDailyLimit] = useState('300');
  const [batchSize, setBatchSize] = useState('1');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLimitConfig();
  }, []);

  const fetchLimitConfig = async () => {
    try {
      const { data: limitData, error: limitError } = await supabase
        .from('site_configurations')
        .select('value')
        .eq('key', 'daily_generation_limit')
        .single();

      const { data: batchData, error: batchError } = await supabase
        .from('site_configurations')
        .select('value')
        .eq('key', 'automation_default_batch_size')
        .single();

      if (limitError) throw limitError;
      if (limitData) {
        setDailyLimit(limitData.value);
      }
      
      if (!batchError && batchData) {
        setBatchSize(batchData.value);
      }
    } catch (error) {
      console.error('Error fetching limit config:', error);
      toast.error('Failed to load limit configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const limitValue = parseInt(dailyLimit);
      const batchValue = parseInt(batchSize);
      
      if (isNaN(limitValue) || limitValue < 1) {
        toast.error('Please enter a valid daily limit greater than 0');
        return;
      }
      
      if (isNaN(batchValue) || batchValue < 1 || batchValue > 10) {
        toast.error('Batch size must be between 1 and 10');
        return;
      }

      const { error: limitError } = await supabase
        .from('site_configurations')
        .update({ value: dailyLimit, updated_at: new Date().toISOString() })
        .eq('key', 'daily_generation_limit');

      const { error: batchError } = await supabase
        .from('site_configurations')
        .update({ value: batchSize, updated_at: new Date().toISOString() })
        .eq('key', 'automation_default_batch_size');

      if (limitError) throw limitError;
      if (batchError) throw batchError;

      toast.success('Limit settings updated successfully');
      await fetchLimitConfig();
    } catch (error) {
      console.error('Error saving limits:', error);
      toast.error('Failed to update limits');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle>User Generation Limits</CardTitle>
        <CardDescription>
          Configure daily generation limits for users
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="daily-limit">Daily Generation Limit</Label>
          <Input
            id="daily-limit"
            type="number"
            min="1"
            value={dailyLimit}
            onChange={(e) => setDailyLimit(e.target.value)}
            placeholder="300"
          />
          <p className="text-sm text-muted-foreground">
            Maximum number of images a user can generate per day
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="batch-size">Automation Default Batch Size (N)</Label>
          <Input
            id="batch-size"
            type="number"
            min="1"
            max="10"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            placeholder="1"
          />
          <p className="text-sm text-muted-foreground">
            Number of images per API request (1-10). Note: Edge function limits processing to 3 images per invocation to prevent resource exhaustion.
          </p>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Limit
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
